import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Truck, FileText, Receipt, Files, FileUp, Image as ImageIcon, Camera, ArrowLeft, ArrowRight, Trash2, Loader2, Upload, GripVertical, Layers, Split } from "lucide-react";

const toB64 = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
const PART_LABEL = { dn: "Delivery Note", inv: "Tax Invoice", combined: "Combined Delivery Note & Tax Invoice" };
const MAX_MB = 15;

function Choice({ icon: Icon, title, desc, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="flex w-full items-center gap-3 rounded-xl border border-border bg-white p-4 text-left transition-colors hover:border-[#1B4383] hover:bg-[#F4F7FC] active:bg-[#E6ECF5]">
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#E6ECF5] text-[#1B4383]"><Icon className="h-5 w-5" /></span>
      <span className="min-w-0"><span className="block text-sm font-semibold text-slate-800">{title}</span>{desc && <span className="block text-xs text-muted-foreground">{desc}</span>}</span>
    </button>
  );
}

function PageSorter({ pages, setPages }) {
  const [urls, setUrls] = useState([]);
  const drag = useRef(null);
  useEffect(() => {
    const u = pages.map((p) => URL.createObjectURL(p));
    setUrls(u);
    return () => { u.forEach((x) => URL.revokeObjectURL(x)); };
  }, [pages]);
  const move = (from, to) => { if (to < 0 || to >= pages.length || from === to) return; setPages((p) => { const n = [...p]; const [x] = n.splice(from, 1); n.splice(to, 0, x); return n; }); };
  const onMove = (e) => {
    if (drag.current === null) return;
    const el = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-idx]");
    const to = el ? Number(el.dataset.idx) : null;
    if (to !== null && to !== drag.current) { move(drag.current, to); drag.current = to; }
  };
  if (!pages.length) return null;
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" onPointerMove={onMove} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }} data-testid="dw-pages">
      {urls.map((u, i) => (
        <div key={u} data-idx={i} className="relative overflow-hidden rounded-lg border border-border bg-white" data-testid={`dw-page-${i}`}>
          <div onPointerDown={(e) => { e.preventDefault(); drag.current = i; }} className="flex cursor-grab touch-none items-center justify-center gap-1 bg-slate-100 py-1 text-[10px] font-semibold text-slate-600 active:cursor-grabbing" data-testid={`dw-page-drag-${i}`}><GripVertical className="h-3 w-3" />Page {i + 1}</div>
          <img src={u} alt={`Page ${i + 1}`} className="pointer-events-none h-28 w-full object-cover" draggable={false} />
          <div className="flex items-center justify-between px-1 py-1">
            <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="rounded p-1 text-slate-500 disabled:opacity-30" aria-label="Move Page Left" data-testid={`dw-page-left-${i}`}><ArrowLeft className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => setPages((p) => p.filter((_, k) => k !== i))} className="rounded p-1 text-slate-400 hover:text-red-600" aria-label="Remove Page" data-testid={`dw-page-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => move(i, i + 1)} disabled={i === pages.length - 1} className="rounded p-1 text-slate-500 disabled:opacity-30" aria-label="Move Page Right" data-testid={`dw-page-right-${i}`}><ArrowRight className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Collector({ method, value, onChange }) {
  const pages = value?.pages || [];
  const setPages = (fn) => onChange({ pages: typeof fn === "function" ? fn(pages) : fn });
  const addFiles = (e) => {
    const f = [...(e.target.files || [])]; e.target.value = "";
    if (f.some((x) => x.size > MAX_MB * 1024 * 1024)) { toast.error(`Each File Must Be Under ${MAX_MB} MB`); return; }
    if (f.length) setPages((p) => [...p, ...f].slice(0, 20));
  };
  if (method === "pdf") {
    return (
      <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#1B4383]/30 bg-[#F4F7FC] p-6 text-center" data-testid="dw-pdf-drop">
        <FileUp className="h-7 w-7 text-[#1B4383]" />
        <span className="text-sm font-semibold text-[#1B4383]">{value?.file ? value.file.name : "Choose Scanned PDF"}</span>
        <span className="text-[11px] text-muted-foreground">{value?.file ? "Tap To Choose A Different File" : `PDF Up To ${MAX_MB} MB`}</span>
        <input type="file" accept="application/pdf" className="hidden" data-testid="dw-pdf-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (!f) return; if (f.size > MAX_MB * 1024 * 1024) { toast.error(`File Must Be Under ${MAX_MB} MB`); return; } onChange({ file: f }); }} />
      </label>
    );
  }
  const camera = method === "photo";
  return (
    <div className="space-y-3">
      <PageSorter pages={pages} setPages={setPages} />
      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#1B4383] bg-white px-4 py-3 text-sm font-semibold text-[#1B4383] active:bg-[#E6ECF5]" data-testid="dw-add-pages">
        {camera ? <Camera className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />}
        {camera ? (pages.length ? `Take Page ${pages.length + 1} Photo` : "Take Page 1 Photo") : (pages.length ? "Add More Images" : "Choose Images")}
        {camera
          ? <input type="file" accept="image/*" capture="environment" className="hidden" onChange={addFiles} data-testid="dw-photo-input" />
          : <input type="file" accept="image/*" multiple className="hidden" onChange={addFiles} data-testid="dw-image-input" />}
      </label>
      {pages.length > 1 && <p className="text-center text-[11px] text-muted-foreground">Drag A Page By Its Label, Or Use The Arrows, To Put Pages In The Right Order.</p>}
    </div>
  );
}

function partsFor(kind, layout) {
  if (kind === "both") return layout === "separate" ? ["dn", "inv"] : ["combined"];
  return [kind];
}

export function DeliveryWizard({ open, onOpenChange, postUrl, onDone }) {
  const [step, setStep] = useState("kind");
  const [kind, setKind] = useState("");
  const [layout, setLayout] = useState("");
  const [idx, setIdx] = useState(0);
  const [methods, setMethods] = useState({});
  const [files, setFiles] = useState({});
  const [meta, setMeta] = useState({ note: "", is_final: false, dn_number: "", inv_number: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setStep("kind"); setKind(""); setLayout(""); setIdx(0); setMethods({}); setFiles({}); setMeta({ note: "", is_final: false, dn_number: "", inv_number: "" }); }
  }, [open]);

  const parts = partsFor(kind, layout);
  const part = parts[idx];
  const needDn = parts.includes("dn") || parts.includes("combined");
  const needInv = parts.includes("inv") || parts.includes("combined");
  const numsOk = (!needDn || meta.dn_number.trim()) && (!needInv || meta.inv_number.trim());
  const clean = (v, pre) => v.trim().replace(new RegExp(`^(SOM-)?${pre}[-\\s#]*`, "i"), "").replace(/[^A-Za-z0-9-]+/g, "");
  const preview = `SOM-${[needDn && `DN-${clean(meta.dn_number, "DN") || "____"}`, needInv && `INV-${clean(meta.inv_number, "INV") || "____"}`].filter(Boolean).join("&")}`;
  const ready = (p) => (methods[p] === "pdf" ? !!files[p]?.file : (files[p]?.pages || []).length > 0);
  const pickKind = (k) => { setKind(k); setIdx(0); setStep(k === "both" ? "layout" : "method"); };
  const back = () => {
    if (step === "confirm") { setIdx(parts.length - 1); setStep("collect"); }
    else if (step === "collect") setStep("method");
    else if (step === "method") { if (idx > 0) { setIdx(idx - 1); setStep("collect"); } else setStep(kind === "both" ? "layout" : "kind"); }
    else if (step === "layout") setStep("kind");
  };
  const next = () => { if (idx < parts.length - 1) { setIdx(idx + 1); setStep("method"); } else setStep("confirm"); };

  const send = async () => {
    setBusy(true);
    try {
      const payload = { note: meta.note, is_final: meta.is_final, dn_number: needDn ? meta.dn_number : "", inv_number: needInv ? meta.inv_number : "" };
      for (const p of parts) {
        if (methods[p] === "pdf") { const f = files[p].file; payload[`${p}_file`] = await toB64(f); payload[`${p}_filename`] = f.name; payload[`${p}_content_type`] = f.type || "application/pdf"; }
        else payload[`${p}_pages`] = await Promise.all(files[p].pages.map((x) => toB64(x)));
      }
      await api.post(postUrl, payload);
      toast.success("Delivery Recorded & Sent To The Sales Order");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const titles = { kind: "Start Delivery", layout: "Both Documents", method: `Upload ${PART_LABEL[part] || ""}`, collect: PART_LABEL[part] || "", confirm: "Review & Send" };
  const descs = { kind: "What Are You Uploading?", layout: "Are They In One Document Or Two Separate Documents?", method: "How Do You Want To Add It?", collect: methods[part] === "pdf" ? "Attach The Scanned PDF." : "Add Every Page. Each Image Becomes One Page Of A Single PDF.", confirm: "Check Everything Below, Then Send It To The Sales Order." };
  const stepNo = { kind: 1, layout: 1, method: 2, collect: 2, confirm: 3 }[step];

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="delivery-wizard">
        <DialogHeader>
          <div className="text-[10px] font-semibold tracking-wide text-[#1B4383]" data-testid="dw-step">Step {stepNo} Of 3{parts.length > 1 && ["method", "collect"].includes(step) ? ` · Document ${idx + 1} Of ${parts.length}` : ""}</div>
          <DialogTitle data-testid="dw-title">{titles[step]}</DialogTitle>
          <DialogDescription>{descs[step]}</DialogDescription>
        </DialogHeader>

        {step === "kind" && (
          <div className="space-y-2">
            <Choice icon={FileText} title="Delivery Note" desc="Signed SOM-DN Only" onClick={() => pickKind("dn")} testid="dw-kind-dn" />
            <Choice icon={Receipt} title="Tax Invoice" desc="SOM-INV Only" onClick={() => pickKind("inv")} testid="dw-kind-inv" />
            <Choice icon={Files} title="Both" desc="Delivery Note And Tax Invoice" onClick={() => pickKind("both")} testid="dw-kind-both" />
          </div>
        )}

        {step === "layout" && (
          <div className="space-y-2">
            <Choice icon={Layers} title="One Combined Document" desc="DN And Invoice Are In The Same File Or Stack Of Pages" onClick={() => { setLayout("combined"); setIdx(0); setStep("method"); }} testid="dw-layout-combined" />
            <Choice icon={Split} title="Two Separate Documents" desc="Upload The DN First, Then The Invoice" onClick={() => { setLayout("separate"); setIdx(0); setStep("method"); }} testid="dw-layout-separate" />
          </div>
        )}

        {step === "method" && (
          <div className="space-y-2">
            {[["pdf", FileUp, "Scanned PDF", "Upload A PDF From This Device"], ["image", ImageIcon, "Image(s)", "Choose One Or More Pictures From The Gallery"], ["photo", Camera, "Take Photo(s)", "Use The Camera, One Photo Per Page"]].map(([m, Icon, t, d]) => (
              <Choice key={m} icon={Icon} title={t} desc={d} testid={`dw-method-${m}`} onClick={() => { if (methods[part] !== m) setFiles((f) => ({ ...f, [part]: null })); setMethods((x) => ({ ...x, [part]: m })); setStep("collect"); }} />
            ))}
          </div>
        )}

        {step === "collect" && (
          <div className="space-y-4">
            <Collector method={methods[part]} value={files[part]} onChange={(v) => setFiles((f) => ({ ...f, [part]: v }))} />
            <Button onClick={next} disabled={!ready(part)} className="w-full bg-[#1B4383] hover:bg-[#16386e]" data-testid="dw-next">
              {idx < parts.length - 1 ? `Next: ${PART_LABEL[parts[idx + 1]]}` : "Continue"}<ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-4">
            <div className="space-y-1.5 rounded-lg border border-border bg-slate-50/60 p-3" data-testid="dw-summary">
              {parts.map((p) => (
                <div key={p} className="flex items-center justify-between gap-2 text-sm"><span className="font-medium text-slate-800">{PART_LABEL[p]}</span><span className="text-xs text-muted-foreground">{methods[p] === "pdf" ? files[p].file.name : `${files[p].pages.length} Page${files[p].pages.length > 1 ? "s" : ""} → One PDF`}</span></div>
              ))}
            </div>
            <div className="space-y-2 rounded-lg border border-[#1B4383]/20 bg-[#F4F7FC] p-3" data-testid="dw-numbers">
              <div className="text-xs font-semibold text-[#1B4383]">Document Number{needDn && needInv ? "s" : ""}</div>
              <div className={`grid gap-2 ${needDn && needInv ? "grid-cols-2" : ""}`}>
                {needDn && <div className="space-y-1"><Label className="text-xs">DN Number</Label><Input value={meta.dn_number} onChange={(e) => setMeta({ ...meta, dn_number: e.target.value })} placeholder="e.g. 0045" inputMode="numeric" data-testid="dw-dn-number" /></div>}
                {needInv && <div className="space-y-1"><Label className="text-xs">INV Number</Label><Input value={meta.inv_number} onChange={(e) => setMeta({ ...meta, inv_number: e.target.value })} placeholder="e.g. 0045" inputMode="numeric" data-testid="dw-inv-number" /></div>}
              </div>
              <p className="break-all text-[11px] text-muted-foreground">File Title: <span className="font-mono text-slate-700" data-testid="dw-title-preview">{preview}_PO_CLIENT</span></p>
            </div>
            <div className="space-y-1"><Label className="text-xs">Note (Optional)</Label><Input value={meta.note} onChange={(e) => setMeta({ ...meta, note: e.target.value })} data-testid="dw-note" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={meta.is_final} onChange={(e) => setMeta({ ...meta, is_final: e.target.checked })} data-testid="dw-final" />Final Delivery — Mark The Sales Order Complete</label>
            <Button onClick={send} disabled={busy || !numsOk} className="w-full bg-[#1B4383] hover:bg-[#16386e]" data-testid="dw-send">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}Send To Sales Order</Button>
          </div>
        )}

        {step !== "kind" && <button type="button" onClick={back} disabled={busy} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800" data-testid="dw-back"><ArrowLeft className="h-3.5 w-3.5" />Back</button>}
      </DialogContent>
    </Dialog>
  );
}

export function StartDeliveryButton({ onClick, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid="start-delivery-btn" className="flex w-full items-center justify-center gap-3 rounded-xl bg-[#1B4383] px-6 py-5 text-base font-bold text-white shadow-sm transition-colors hover:bg-[#16386e] active:bg-[#122f5c] disabled:opacity-50">
      <Truck className="h-6 w-6" />Start Delivery
    </button>
  );
}
